import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Camera, Save, ArrowRight, FileText, LayoutGrid, Coins, AlignLeft, Image as ImageIcon, Trash2, ListOrdered, Package, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AddProduct = () => {
  const navigate = useNavigate();
  
  // --- حالات المنتج (Product States) ---
  const [files, setFiles] = useState([]); // مصفوفة للصور المتعددة
  const [previews, setPreviews] = useState([]); // مصفوفة لمعاينة الصور
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', 
    category: 'baby-kids', 
    price: '', 
    price_syp: '', 
    stock: '', 
    description: '' 
  });

  // --- حالات السلايدر (Slider States) ---
  const [sliderFile, setSliderFile] = useState(null);
  const [sliderPreview, setSliderPreview] = useState(null);
  const [isSliderSubmitting, setIsSliderSubmitting] = useState(false);
  
  const [sliderData, setSliderData] = useState({ title: '', sub_title: '' });
  
  // --- حالة إدارة السلايدر ---
  const [existingSliders, setExistingSliders] = useState([]);

  // روابط API الخاصة بكِ على Render
  const SLIDERS_API = 'https://my-pharmacy-api.onrender.com/api/sliders';
  const PRODUCTS_API = 'https://my-pharmacy-api.onrender.com/api/products';

  // وظيفة مساعدة لعرض الصور
  const getImageUrl = (url) => {
    if (!url) return "https://via.placeholder.com/400?text=No+Image";
    if (url.startsWith('http')) return url;
    return `https://my-pharmacy-api.onrender.com${url}`;
  };

  const fetchSliders = async () => {
    try {
      const res = await axios.get(SLIDERS_API);
      setExistingSliders(res.data);
    } catch (err) {
      console.error("خطأ في جلب السلايدر:", err);
    }
  };

  useEffect(() => {
    fetchSliders();
  }, []);

  const handleDeleteSlider = async (id) => {
    if (!window.confirm("هل أنتِ متأكدة من حذف هذا الإعلان نهائياً؟")) return;
    try {
      await axios.delete(`${SLIDERS_API}/${id}`);
      setExistingSliders(existingSliders.filter(s => s.id !== id));
      alert("✅ تم الحذف بنجاح");
    } catch (err) {
      alert("❌ فشل حذف الإعلان");
    }
  };

  // معالجة اختيار صور متعددة للمنتج
  const handleImagesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFiles(selectedFiles);
      const filePreviews = selectedFiles.map(file => URL.createObjectURL(file));
      setPreviews(filePreviews);
    }
  };

  const handleSliderImageChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setSliderFile(selectedFile);
      setSliderPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) { alert("يرجى اختيار صورة واحدة على الأقل للدواء"); return; }
    setIsSubmitting(true);

    const data = new FormData();
    
    // إرسال الصور المتعددة للسيرفر تحت اسم 'images'
    files.forEach((file) => {
      data.append('images', file);
    });

    Object.keys(formData).forEach(key => data.append(key, formData[key]));

    try {
      await axios.post(PRODUCTS_API, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert("✅ تم رفع المنتج وكافة الصور إلى Cloudinary بنجاح!");
      navigate('/admin'); 
    } catch (err) {
      console.error(err);
      alert("❌ فشل في رفع البيانات - تأكدي من تحديث السيرفر");
    } finally { setIsSubmitting(false); }
  };

  const handleSliderSubmit = async (e) => {
    e.preventDefault();
    if (!sliderFile) { alert("يرجى اختيار صورة للمنصة أولاً"); return; }
    setIsSliderSubmitting(true);
    
    const data = new FormData();
    data.append('image', sliderFile); 
    data.append('title', sliderData.title);
    data.append('sub_title', sliderData.sub_title); 

    try {
      await axios.post(SLIDERS_API, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert("✅ تم إضافة صورة العرض بنجاح!");
      setSliderFile(null); 
      setSliderPreview(null);
      setSliderData({ title: '', sub_title: '' });
      fetchSliders();
    } catch (err) {
      console.error(err);
      alert("❌ فشل رفع صورة العرض");
    } finally { setIsSliderSubmitting(false); }
  };

  return (
    <div className="max-w-xl mx-auto pb-32 px-4 transition-colors duration-300" dir="rtl">
      
      {/* الرأس */}
      <div className="flex items-center justify-between mb-8 pt-4">
        <h2 className="text-2xl font-black text-gray-800 dark:text-white tracking-tight">لوحة تحكم المحتوى</h2>
        <button onClick={() => navigate('/admin')} className="p-2 bg-white dark:bg-slate-800 rounded-full shadow-sm text-gray-400 hover:text-blue-600 transition-all">
          <ArrowRight size={24} />
        </button>
      </div>

      {/* --- القسم الأول: إضافة منتج --- */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-6 border-r-4 border-blue-600 pr-3">
            <h3 className="text-xl font-black text-gray-700 dark:text-gray-200">إضافة دواء جديد</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50/50 dark:bg-slate-900/50 p-6 rounded-[40px] border border-gray-100 dark:border-slate-800">
          
          {/* رفع صور المنتج المتعددة */}
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-blue-100 dark:border-slate-700 rounded-[35px] p-6 bg-white dark:bg-slate-900 shadow-sm hover:border-blue-400 transition-all group relative overflow-hidden">
            {previews.length > 0 ? (
              <div className="grid grid-cols-3 gap-2 z-10">
                {previews.map((src, index) => (
                  <img key={index} src={src} alt="Preview" className="w-20 h-20 object-cover rounded-xl shadow-md border-2 border-white dark:border-slate-800" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-4 text-center">
                <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center text-blue-500 mb-3 group-hover:scale-110 transition-transform">
                  <Camera size={32} />
                </div>
                <p className="text-gray-400 dark:text-slate-500 text-xs font-bold">يمكنك اختيار عدة صور معاً</p>
              </div>
            )}
            <label className="mt-4 bg-gray-900 dark:bg-blue-600 text-white px-8 py-3 rounded-[20px] text-sm font-bold cursor-pointer hover:bg-blue-600 transition-all shadow-lg active:scale-95">
              {previews.length > 0 ? "إعادة اختيار الصور" : "تحميل الصور"}
              <input type="file" className="hidden" onChange={handleImagesChange} accept="image/*" multiple />
            </label>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-black text-gray-500 dark:text-slate-400 flex items-center gap-2 mr-1">
              <FileText size={16} className="text-blue-500" /> اسم الدواء
            </label>
            <input type="text" required value={formData.name} placeholder="مثال: آي في آي جي 5غ"
              className="w-full p-4 bg-white dark:bg-slate-900 dark:text-white rounded-[22px] border-none shadow-sm outline-none focus:ring-2 focus:ring-blue-500 font-bold"
              onChange={e => setFormData({...formData, name: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-black text-gray-500 dark:text-slate-400 flex items-center gap-2 mr-1">
              <LayoutGrid size={16} className="text-blue-500" /> التصنيف
            </label>
            <select className="w-full p-4 bg-white dark:bg-slate-900 dark:text-white rounded-[22px] border-none shadow-sm outline-none focus:ring-2 focus:ring-blue-500 font-bold cursor-pointer"
              onChange={e => setFormData({...formData, category: e.target.value})} value={formData.category}>
              <option value="baby-kids">بيبي و أطفال </option>
              <option value="Supplements">مكملات غذائية</option>
              <option value="Cosmetics-Beauty">مستحضرات تجميل</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-black text-gray-500 dark:text-slate-400 flex items-center gap-2 mr-1">
              <Package size={16} className="text-blue-500" /> الكمية المتوفرة
            </label>
            <input type="number" required value={formData.stock} placeholder="مثال: 50"
              className="w-full p-4 bg-white dark:bg-slate-900 dark:text-white rounded-[22px] border-none shadow-sm outline-none focus:ring-2 focus:ring-blue-500 font-bold"
              onChange={e => setFormData({...formData, stock: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 flex items-center gap-2"><Coins size={14}/> دولار</label>
              <input type="number" required placeholder="0.00" value={formData.price}
                className="w-full p-4 bg-white dark:bg-slate-900 rounded-[22px] shadow-sm font-bold text-blue-600 outline-none focus:ring-2 focus:ring-blue-500"
                onChange={e => setFormData({...formData, price: e.target.value})} />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 flex items-center gap-2"><Coins size={14}/> ليرة</label>
              <input type="number" required placeholder="0" value={formData.price_syp}
                className="w-full p-4 bg-white dark:bg-slate-900 rounded-[22px] shadow-sm font-bold text-green-600 outline-none focus:ring-2 focus:ring-blue-500"
                onChange={e => setFormData({...formData, price_syp: e.target.value})} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-black text-gray-500 dark:text-slate-400 flex items-center gap-2 mr-1">
              <AlignLeft size={16} className="text-blue-500" /> وصف الدواء
            </label>
            <textarea value={formData.description} placeholder="اكتب تفاصيل الدواء هنا..." rows="3"
              className="w-full p-4 bg-white dark:bg-slate-900 dark:text-white rounded-[22px] border-none shadow-sm outline-none focus:ring-2 focus:ring-blue-500 font-bold resize-none"
              onChange={e => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-5 rounded-[28px] font-black shadow-2xl active:scale-95 transition-all">
            {isSubmitting ? "جاري الرفع..." : "تأكيد وحفظ الدواء"}
          </button>
        </form>
      </section>

      <hr className="my-10 border-gray-200 dark:border-slate-800" />

      {/* --- القسم الثاني: إضافة صورة عرض (Slider) --- */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-6 border-r-4 border-pink-600 pr-3">
            <h3 className="text-xl font-black text-gray-700 dark:text-gray-200">إضافة إعلان جديد</h3>
        </div>

        <form onSubmit={handleSliderSubmit} className="space-y-6 bg-pink-50/30 dark:bg-pink-900/5 p-6 rounded-[40px] border border-pink-100 dark:border-pink-900/20">
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-pink-200 dark:border-pink-900/30 rounded-[35px] p-6 bg-white dark:bg-slate-900 shadow-sm transition-all group relative overflow-hidden">
            {sliderPreview ? (
                <img src={sliderPreview} alt="Slider Preview" className="w-full h-32 object-cover rounded-[20px] shadow-md" />
            ) : (
              <div className="flex flex-col items-center py-2 text-center">
                <ImageIcon size={32} className="text-pink-400 mb-2" />
                <p className="text-gray-400 text-xs font-bold">صورة الإعلان (16:9)</p>
              </div>
            )}
            <label className="mt-4 bg-pink-600 text-white px-6 py-2.5 rounded-[15px] text-xs font-black cursor-pointer shadow-md">
              اختيار صورة
              <input type="file" className="hidden" onChange={handleSliderImageChange} accept="image/*" />
            </label>
          </div>

          <input type="text" placeholder="عنوان الإعلان" value={sliderData.title}
            className="w-full p-4 bg-white dark:bg-slate-900 dark:text-white rounded-[20px] border-none shadow-sm font-bold text-sm"
            onChange={e => setSliderData({...sliderData, title: e.target.value})} />

          <input type="text" placeholder="وصف قصير" value={sliderData.sub_title}
            className="w-full p-4 bg-white dark:bg-slate-900 dark:text-white rounded-[20px] border-none shadow-sm font-bold text-sm"
            onChange={e => setSliderData({...sliderData, sub_title: e.target.value})} />

          <button type="submit" disabled={isSliderSubmitting} className="w-full bg-pink-600 text-white py-4 rounded-[22px] font-black active:scale-95 transition-all">
            {isSliderSubmitting ? "جاري الرفع..." : "إضافة الإعلان الآن"}
          </button>
        </form>
      </section>

      {/* --- القسم الثالث: إدارة الإعلانات المرفوعة --- */}
      <section className="bg-white dark:bg-slate-900 p-6 rounded-[40px] shadow-sm border border-gray-100 dark:border-slate-800">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <ListOrdered className="text-pink-500" size={20} />
            <h3 className="font-black text-gray-700 dark:text-white">الإعلانات الحالية</h3>
          </div>
          <span className="bg-pink-100 text-pink-600 px-3 py-1 rounded-full text-xs font-black">
            {existingSliders.length} إعلانات
          </span>
        </div>

        <div className="space-y-4">
          {existingSliders.length === 0 ? (
            <p className="text-center text-gray-400 py-4 text-sm font-bold italic">لا توجد إعلانات مرفوعة حالياً</p>
          ) : (
            existingSliders.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-[25px] border border-gray-100 dark:border-slate-700">
                <img src={getImageUrl(item.image_url)} alt="" className="w-16 h-16 object-cover rounded-[15px]" />
                <div className="flex-1 overflow-hidden">
                  <h4 className="text-sm font-black text-gray-800 dark:text-white truncate">{item.title || "بدون عنوان"}</h4>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{item.sub_title || "بدون وصف"}</p>
                </div>
                <button 
                  onClick={() => handleDeleteSlider(item.id)}
                  className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-all"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))
          )}
        </div>
      </section>

    </div>
  );
};

export default AddProduct;